import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPreSignedUrlToUpload } from '../../../../../../../redux/Spoolsbase/saga';
import { AddMaterialCategory, getListMaterialCategory } from '../../../../../../../redux/Project/materialSaga';
import { Notification } from '../../../../../../../components/ui/custom-notification';
import { fetchSpoolNcrList } from '../../../../../../../redux/Spoolsbase/actions';
import { filterNcrRowsByWeldId, type NcrRow } from '../../spoolNcrApi';
import { WorkflowNcrSummaryTable } from '../weld/WorkflowNcrSummaryTable';
import type { WeldJoint } from '../../SpoolsProduction';
import { Button } from '../../../../../../../components/ui/button';
import { Input } from '../../../../../../../components/ui/input';
import { AntdDate } from '../../../../../../../components/ui/antd-date';
import { Label } from '../../../../../../../components/ui/label';
import AddNewItemDialog from '../../../../../../../components/ui/AddNewItemDialog';
import { SearchableSelect } from '../../../../../../../components/ui/SearchableSelect';
import { FileUpload } from '../../../../../../../components/ui/FileUpload';
import { Signature } from '../../Signature';
import type { SignaturePanelProps } from '../weld/signatureTypes';
import {
  FieldGroup,
  FormGrid,
  SectionTitle,
  StepInfoBanner,
} from '../weld/spoolDetailFormUi';
import {
  buildCoatingInspectorPayload,
  fileListToMeta,
  mergeSpoolStepDocumentsByS3Key,
  normalizeSpoolStepDocumentsForSave,
  parseDocumentsFromStepPayload,
  parseOptionalFloat,
  type SpoolStepPanelContext,
} from '../weld/spoolStepPayloadHelpers';
import { SpoolStepSavedDocumentsList } from '../weld/SpoolStepSavedDocumentsList';
import {
  getLastCoatingPanelRow,
  getWeldStepPanelPayload,
  weldPanelsRevision,
} from '../weld/weldStepPanelHydration';
import { Card } from '../../../../../../../components/ui/card';
import { CardContent } from '../../../../../../../components/ui/card';
import type { DocumentTag } from '@/features/project/types';
import {
  listCoatingSpecificationTemplates,
  type CoatingSpecificationTemplateDto,
} from '../../../specifications/Coating/coatingSpecificationTemplateApi';
import type { CoatingChecklistTemplateSection } from '../../../specifications/Coating/coatingChecklistMockData';
import type { CoatingSpoolPersistPayload } from '../../../specifications/Coating/CoatingSpoolChecklistEditor';
import { ProductionChecklistCards } from './ProductionChecklistCards';
  
export type CoatingSubmissionMode = 'draft' | 'pass_release';

/** Coating attachments after S3 upload via presign (`getPresignedUrlToUploadSpoolsbaseDoc`). */
export interface CoatingUploadedDocumentPayload {
  fileName: string;
  s3Key: string;
  uploadedBy: string;
  documentCategory?: string;
  tags?: string[];
  spoolDocId?: number;
}

export interface CoatingSavePayload {
  mode: CoatingSubmissionMode;
  /** API parity (`draft` | `submit`); `pass_release` maps to `submit`. */
  submissionMode?: 'draft' | 'submit';
  projectId?: number | string | null;
  pipeId?: number | string | null;
  spoolId?: number | null;
  spoolName?: string | null;
  weldId?: string | null;
  weldNumber?: string | null;
  areaType: string;
  coatingProcedure: string;
  inspectionDate: string;
  ambientTempC: string;
  ambientTempCNum: number | null;
  humidityPercent: string;
  humidityPercentNum: number | null;
  coatingBatchNumber: string;
  /**
   * Per-item values from the coating checklist template, keyed by checklist item id.
   * Template categories/items come from `/fat-specification-templates` (`category = COATING_CHECKLIST_TEMPLATE`).
   */
  checklistValues: Record<string, string>;
  /** Per-item free-text notes captured by the inspector, keyed by checklist item id. */
  checklistNotes: Record<string, string>;
  /** Snapshot of which coating-checklist template row this weld saved against (for reporting). */
  coatingChecklistTemplateId?: number;
  notes: string;
  attachmentsMeta: ReturnType<typeof fileListToMeta>;
  coatingInspector: ReturnType<typeof buildCoatingInspectorPayload>;
  documents?: CoatingUploadedDocumentPayload[];
  /** Existing weld-step row id — required for UPDATE so documents stay on the same row as VT/Welding. */
  stepPanelRowId?: number;
}

const DEFAULT_AREA_TYPE_LABELS = ['Weld FBE Tie-In', 'Bare Cutback', 'Bend'] as const;

/** Legacy slugs (`fbe` / `bare` / `bend`) → display labels; save payload uses label text. */
function normalizeAreaTypeDisplay(raw?: string): string {
  if (raw == null || String(raw).trim() === '') return 'Weld FBE Tie-In';
  const s = String(raw).trim();
  const lower = s.toLowerCase();
  if (lower === 'fbe') return 'Weld FBE Tie-In';
  if (lower === 'bare') return 'Bare Cutback';
  if (lower === 'bend') return 'Bend';
  if (
    s === 'Weld FBE Tie-In' ||
    s === 'Bare Cutback' ||
    s === 'Bend'
  ) {
    return s;
  }
  return s;
}

function coatingPayloadField(pl: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    if (k in pl && pl[k] != null) return pl[k];
  }
  return undefined;
}

export interface CoatingPanelProps {
  weld?: WeldJoint | null;
  sig: SignaturePanelProps;
  context?: SpoolStepPanelContext;
  onCoatingSave?: (payload: CoatingSavePayload) => void | Promise<void>;
  onSubmittedNcrClick?: (row: NcrRow) => void;
  /** Opens Raise NCR for the coating step (no coating save). */
  onOpenRaiseNcr?: () => void;
  repairNo?: number | null;
}

export function CoatingPanel({
  weld,
  sig,
  context,
  onCoatingSave,
  onSubmittedNcrClick,
  onOpenRaiseNcr,
  repairNo = null,
}: CoatingPanelProps) {
  const dispatch = useDispatch();
  const ncrListAll = useSelector((s: { spoolsbase?: { ncrList?: NcrRow[] } }) => s.spoolsbase?.ncrList ?? []);
  const ncrList = useMemo(
    () =>
      filterNcrRowsByWeldId(ncrListAll, weld?.id ?? context?.weldId ?? null, {
        matchStepType: 'coating',
      }),
    [ncrListAll, weld?.id, context?.weldId]
  );
  const ncrListLoading = useSelector(
    (s: { spoolsbase?: { ncrListLoading?: boolean } }) => s.spoolsbase?.ncrListLoading ?? false
  );
  const [files, setFiles] = useState<File[]>([]);
  const [documentTags, setDocumentTags] = useState<DocumentTag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[][]>([]);
  const [isAddingDocTag, setIsAddingDocTag] = useState(false);
  const [areaType, setAreaType] = useState('Weld FBE Tie-In');
  const [extraAreaTypeLabels, setExtraAreaTypeLabels] = useState<string[]>([]);
  const [isAddingAreaType, setIsAddingAreaType] = useState(false);
  const [coatingProcedure, setCoatingProcedure] = useState('CPC-001 (NORSOK M-501 7C)');
  const [inspectionDate, setInspectionDate] = useState('');
  const [ambientTempC, setAmbientTempC] = useState('');
  const [humidityPercent, setHumidityPercent] = useState('');
  const [coatingBatchNumber, setCoatingBatchNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [activeSubmit, setActiveSubmit] = useState<CoatingSubmissionMode | null>(null);
  const activeSubmitRef = useRef<CoatingSubmissionMode | null>(null);
  const coatingRemoteSaving = useSelector(
    (s: { spoolsbase?: { isSaving?: boolean; savingStep?: string | null } }) =>
      Boolean(s.spoolsbase?.isSaving && s.spoolsbase?.savingStep === 'coating')
  );
  const authUser = useSelector((s: { auth?: { user?: { email?: string } } }) => s.auth?.user);

  useEffect(() => {
    const pid = context?.projectId;
    const poid = context?.pipeId;
    if (pid == null || pid === '' || poid == null || poid === '') return;
    void dispatch(
      fetchSpoolNcrList({ projectId: pid, pipeId: poid, spoolId: context?.spoolId ?? undefined })
    );
  }, [context?.projectId, context?.pipeId, context?.spoolId, dispatch]);

  useEffect(() => {
    let cancelled = false;
    const loadTags = async () => {
      try {
        const tags = await getListMaterialCategory();
        if (!cancelled && tags && Array.isArray(tags)) {
          setDocumentTags(tags);
        }
      } catch {
        /* ignore */
      }
    };
    void loadTags();
    return () => {
      cancelled = true;
    };
  }, []);

  const tagOptions = useMemo(
    () =>
      documentTags
        .filter((tag) => tag.tags_type === 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS')
        .map((tag) => ({
          value: tag.tags_id.toString(),
          label: tag.tags_name,
        })),
    [documentTags]
  );

  const coatingPanelRow = useMemo(
    () => getLastCoatingPanelRow(weld ?? null),
    [weld, weldPanelsRevision(weld ?? null)]
  );

  const persistedDocuments = useMemo(() => {
    const pl = getWeldStepPanelPayload(weld ?? null, 'coating');
    const fromPayload = parseDocumentsFromStepPayload(pl ?? undefined);
    const row = getLastCoatingPanelRow(weld ?? null) as Record<string, unknown> | null;
    const rowDocs = row?.documents;
    const fromRow =
      Array.isArray(rowDocs) ? parseDocumentsFromStepPayload({ documents: rowDocs }) : [];
    return mergeSpoolStepDocumentsByS3Key(fromPayload, fromRow);
  }, [weld, weldPanelsRevision(weld ?? null)]);

  const downloadContext = useMemo(() => {
    const projectId = Number(context?.projectId);
    const pipeId = Number(context?.pipeId);
    const projectTypeId = Number(context?.projectTypeId);
    const spoolId = Number(context?.spoolId);
    if (![projectId, pipeId, projectTypeId, spoolId].every(Number.isFinite)) return null;
    return { projectId, pipeId, projectTypeId, spoolId };
  }, [context?.projectId, context?.pipeId, context?.projectTypeId, context?.spoolId]);

  const areaTypeSelectOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const name of DEFAULT_AREA_TYPE_LABELS) {
      map.set(name, { id: name, name });
    }
    for (const name of extraAreaTypeLabels) {
      if (!map.has(name)) map.set(name, { id: name, name });
    }
    if (areaType && !map.has(areaType)) {
      map.set(areaType, { id: areaType, name: areaType });
    }
    return Array.from(map.values());
  }, [extraAreaTypeLabels, areaType]);

  const handleAddAreaType = async (name: string) => {
    const v = name.trim();
    if (!v) return;
    setExtraAreaTypeLabels((p) => (p.includes(v) ? p : [...p, v]));
    setAreaType(v);
    setIsAddingAreaType(false);
  };

  /** Per-checklist-item value, keyed by item id. Saved on the weld-step coating row. */
  const [checklistValues, setChecklistValues] = useState<Record<string, string>>({});
  /** Per-checklist-item free-text note (inspector input), keyed by item id. */
  const [checklistNotes, setChecklistNotes] = useState<Record<string, string>>({});

  /**
   * Coating-checklist template loaded from `/fat-specification-templates` (filtered by COATING category).
   * One row per pipe + spool — same shape FAT uses for FAT_CHECKLIST_TEMPLATE.
   */
  const [coatingTemplate, setCoatingTemplate] = useState<CoatingSpecificationTemplateDto | null>(null);
  const [coatingTemplateLoading, setCoatingTemplateLoading] = useState(false);

  useEffect(() => {
    const projectId = context?.projectId;
    const pipeId = context?.pipeId;
    if (projectId == null || projectId === '' || pipeId == null || pipeId === '') {
      setCoatingTemplate(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setCoatingTemplateLoading(true);
      try {
        const res = await listCoatingSpecificationTemplates({ projectId, pipeId });
        if (cancelled) return;
        const all = res.templates ?? [];
        const wantedName = context?.spoolName?.trim();
        const byRef = wantedName
          ? all.find((t) => (t.reference || '').trim() === wantedName)
          : null;
        setCoatingTemplate(byRef ?? all[0] ?? null);
      } catch {
        if (!cancelled) setCoatingTemplate(null);
      } finally {
        if (!cancelled) setCoatingTemplateLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [context?.projectId, context?.pipeId, context?.spoolName]);

  const checklistSections = useMemo<CoatingChecklistTemplateSection[]>(() => {
    const pl = coatingTemplate?.payload as CoatingSpoolPersistPayload | null | undefined;
    return Array.isArray(pl?.checklistSections) ? pl!.checklistSections : [];
  }, [coatingTemplate]);

  /**
   * Baseline per weld, then overlay `weldStepPanels.coating.payload` (draft save + refetch or joint switch).
   */
  useEffect(() => {
    if (!weld) {
      setAreaType('Weld FBE Tie-In');
      setExtraAreaTypeLabels([]);
      setCoatingProcedure('CPC-001 (NORSOK M-501 7C)');
      setInspectionDate('');
      setAmbientTempC('');
      setHumidityPercent('');
      setCoatingBatchNumber('');
      setNotes('');
      setFiles([]);
      setSelectedTags([]);
      setChecklistValues({});
      setChecklistNotes({});
      return;
    }

    setAreaType('Weld FBE Tie-In');
    setExtraAreaTypeLabels([]);
    setCoatingProcedure('CPC-001 (NORSOK M-501 7C)');
    setInspectionDate('');
    setAmbientTempC('');
    setHumidityPercent('');
    setCoatingBatchNumber('');
    setNotes('');
    setFiles([]);
    setSelectedTags([]);
    setChecklistValues({});
    setChecklistNotes({});

    const pl = getWeldStepPanelPayload(weld, 'coating') ?? {};
    if (Object.keys(pl).length === 0) {
      return;
    }

    const at = coatingPayloadField(pl, 'areaType', 'area_type');
    if (at != null) {
      const display = normalizeAreaTypeDisplay(String(at));
      setAreaType(display);
      const def = new Set<string>(DEFAULT_AREA_TYPE_LABELS);
      if (display && !def.has(display)) {
        setExtraAreaTypeLabels((prev) => (prev.includes(display) ? prev : [...prev, display]));
      }
    }
    const cp = coatingPayloadField(
      pl,
      'coatingProcedure',
      'coating_procedure',
      'procedure',
      'coating_proc',
      'wps_ref'
    );
    if (cp != null) setCoatingProcedure(String(cp));
    const id = coatingPayloadField(pl, 'inspectionDate', 'inspection_date');
    if (id != null) setInspectionDate(String(id));
    const a = coatingPayloadField(pl, 'ambientTempC', 'ambient_temp_c', 'ambient_temp');
    if (a != null) setAmbientTempC(String(a));
    const h = coatingPayloadField(pl, 'humidityPercent', 'humidity_percent', 'humidity');
    if (h != null) setHumidityPercent(String(h));
    const b = coatingPayloadField(pl, 'coatingBatchNumber', 'coating_batch_number', 'batch');
    if (b != null) setCoatingBatchNumber(String(b));
    const n = coatingPayloadField(pl, 'notes', 'remarks');
    if (n != null) setNotes(String(n));
    const cv = coatingPayloadField(pl, 'checklistValues', 'checklist_values');
    if (cv && typeof cv === 'object' && !Array.isArray(cv)) {
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(cv as Record<string, unknown>)) {
        next[k] = v == null ? '' : String(v);
      }
      setChecklistValues(next);
    }
    const cn = coatingPayloadField(pl, 'checklistNotes', 'checklist_notes');
    if (cn && typeof cn === 'object' && !Array.isArray(cn)) {
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(cn as Record<string, unknown>)) {
        next[k] = v == null ? '' : String(v);
      }
      setChecklistNotes(next);
    }
  }, [weld, weld?.id, weldPanelsRevision(weld ?? null)]);

  const updateChecklistValue = useCallback(
    (itemId: string, value: string) =>
      setChecklistValues((prev) => ({ ...prev, [itemId]: value })),
    [],
  );

  const updateChecklistNote = useCallback(
    (itemId: string, note: string) =>
      setChecklistNotes((prev) => ({ ...prev, [itemId]: note })),
    [],
  );

  const handleMaterialFileSelect = (selectedFiles: FileList | null) => {
    if (selectedFiles && selectedFiles.length > 0) {
      const fileArray = Array.from(selectedFiles);
      setFiles((prev) => [...prev, ...fileArray]);
      setSelectedTags((prev) => [...prev, ...fileArray.map(() => [] as string[])]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setSelectedTags((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    setSelectedTags((prev) => {
      const next = [...prev];
      next[fileIndex] = tags;
      return next;
    });
  };

  const handleAddDocTag = async (tagName: string) => {
    const v = tagName.trim();
    if (!v) return;
    const pid = context?.projectId;
    if (pid == null || pid === '') {
      Notification.error('Coating', 'Project context required to add tags.');
      return;
    }
    if (
      documentTags.some(
        (tag) =>
          tag.tags_name === v && tag.tags_type === 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS'
      )
    ) {
      Notification.warning('Coating', `${v} tag already exists`);
      return;
    }
    const res = await AddMaterialCategory({
      tags_name: v,
      project_id: Number(pid),
      tags_type: 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS',
      created_by: authUser?.email || '',
    });
    if (res) {
      setDocumentTags((prev) => [...prev, res]);
      setIsAddingDocTag(false);
    }
  };

  const buildPayload = useCallback(
    (mode: CoatingSubmissionMode): CoatingSavePayload => {
      const submissionMode: 'draft' | 'submit' = mode === 'draft' ? 'draft' : 'submit';
      return {
        mode,
        submissionMode,
        projectId: context?.projectId ?? null,
        pipeId: context?.pipeId ?? null,
        spoolId: context?.spoolId ?? null,
        spoolName: context?.spoolName ?? null,
        weldId: context?.weldId ?? null,
        weldNumber: context?.weldNumber ?? null,
        areaType,
        coatingProcedure,
        inspectionDate,
        ambientTempC,
        ambientTempCNum: parseOptionalFloat(ambientTempC),
        humidityPercent,
        humidityPercentNum: parseOptionalFloat(humidityPercent),
        coatingBatchNumber: coatingBatchNumber.trim(),
        checklistValues: { ...checklistValues },
        checklistNotes: { ...checklistNotes },
        ...(coatingTemplate?.id != null
          ? { coatingChecklistTemplateId: coatingTemplate.id }
          : {}),
        notes: notes.trim(),
        attachmentsMeta: fileListToMeta(files),
        coatingInspector: buildCoatingInspectorPayload(sig),
        ...(coatingPanelRow?.id != null && coatingPanelRow.id > 0
          ? { stepPanelRowId: coatingPanelRow.id }
          : {}),
      };
    },
    [
      context,
      areaType,
      coatingProcedure,
      inspectionDate,
      ambientTempC,
      humidityPercent,
      coatingBatchNumber,
      checklistValues,
      checklistNotes,
      coatingTemplate?.id,
      notes,
      files,
      sig,
      coatingPanelRow?.id,
    ]
  );

  const submit = async (mode: CoatingSubmissionMode) => {
    if (!onCoatingSave) return;

    let newUploadDocuments: CoatingUploadedDocumentPayload[] | undefined;

    if (files.length > 0) {
      const projectId = Number(context?.projectId);
      const pipeId = Number(context?.pipeId);
      const projectTypeId = Number(context?.projectTypeId);
      const spoolId = Number(context?.spoolId);
      if (
        !Number.isFinite(projectId) ||
        !Number.isFinite(pipeId) ||
        !Number.isFinite(projectTypeId) ||
        !Number.isFinite(spoolId)
      ) {
        Notification.error(
          'Coating',
          'Missing project, pipe, spool, or project type — cannot upload documents. Open this weld from a spool context.'
        );
        return;
      }

      const uploadPayload = {
        projectId,
        pipeId,
        spoolId,
        projectTypeId,
        type: 'Coating',
      };

      const s3Response = await getPreSignedUrlToUpload(files, uploadPayload, 1);

      if (!s3Response || s3Response.length === 0) {
        Notification.error('Coating', 'Document upload failed. Please try again.');
        return;
      }

      const uploadedBy = authUser?.email?.trim() || '';
      newUploadDocuments = s3Response.map((f: { fileName: string; s3Key: string }, index: number) => ({
        fileName: f.fileName,
        s3Key: f.s3Key,
        uploadedBy,
        documentCategory: 'Coating',
        tags: selectedTags[index] || [],
      }));
    }

    const mergedDocuments =
      newUploadDocuments != null && newUploadDocuments.length > 0
        ? mergeSpoolStepDocumentsByS3Key(persistedDocuments, newUploadDocuments)
        : persistedDocuments;

    const payload: CoatingSavePayload = {
      ...buildPayload(mode),
      ...(mergedDocuments.length > 0
        ? { documents: normalizeSpoolStepDocumentsForSave(mergedDocuments) }
        : {}),
    };

    activeSubmitRef.current = mode;
    setActiveSubmit(mode);
    setFiles([]);
    setSelectedTags([]);
    await Promise.resolve(onCoatingSave(payload));
  };

  useEffect(() => {
    if (!coatingRemoteSaving) {
      activeSubmitRef.current = null;
      setActiveSubmit(null);
    }
  }, [coatingRemoteSaving]);

  const hasExistingCoating = useMemo(() => {
    const pl = getWeldStepPanelPayload(weld ?? null, 'coating');
    return !!pl && Object.keys(pl).length > 0;
  }, [weld?.id, weldPanelsRevision(weld ?? null)]);
  const actionText = hasExistingCoating ? 'Update — Release for Acceptance' : 'Save — Release for Acceptance';
  const releaseLoadingText = hasExistingCoating ? 'Updating…' : 'Saving…';
  const kind = activeSubmit ?? activeSubmitRef.current;
  const savingDraft = coatingRemoteSaving && kind === 'draft';
  const savingRelease = coatingRemoteSaving && kind === 'pass_release';
  const draftDisabled = !weld || savingRelease;
  const releaseDisabled = !weld || savingDraft;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-1  gap-4">
      <Card className="flex max-h-[min(75vh,720px)] min-h-0 flex-col overflow-hidden border border-gray-200">
        <CardContent className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-y-contain p-4">
      {repairNo != null && !Number.isNaN(Number(repairNo)) ? (
        <StepInfoBanner title="Repair coating" variant="notice">
          Repair cycle #{Number(repairNo)} — complete field joint coating again.
        </StepInfoBanner>
      ) : null}
      {ncrList.length > 0 && onSubmittedNcrClick && context?.projectId != null && context?.projectId !== '' && context?.pipeId != null && context?.pipeId !== '' ? (
        <WorkflowNcrSummaryTable
          ncrList={ncrList}
          loading={ncrListLoading}
          step="coating"
          stepTitle="Coating"
          weldId={weld?.id ?? null}
          onRowClick={onSubmittedNcrClick}
        />
      ) : null}
      <FormGrid>
        <FieldGroup label="Area Type" required>
          <SearchableSelect
            value={areaType}
            onValueChange={setAreaType}
            placeholder="Select area type"
            searchPlaceholder="Search area types…"
            options={areaTypeSelectOptions}
            onAddNew={() => setIsAddingAreaType(true)}
            addNewText="Add area type"
            className="w-full [&_button]:h-9 [&_button]:text-sm"
          />
        </FieldGroup>
        <FieldGroup label="Coating Procedure" required>
          <Input
            className="h-9 text-sm"
            placeholder="e.g. CPC-001 (NORSOK M-501 7C)"
            value={coatingProcedure}
            onChange={(e) => setCoatingProcedure(e.target.value)}
          />
        </FieldGroup>
        <FieldGroup label="Inspection Date" required>
          <AntdDate className="h-9 text-sm" value={inspectionDate} onChange={setInspectionDate} />
        </FieldGroup>
        <FieldGroup label="Ambient Temp (°C)">
          <Input
            className="h-9 text-sm"
            placeholder="e.g. 32.5"
            value={ambientTempC}
            onChange={(e) => setAmbientTempC(e.target.value)}
          />
        </FieldGroup>
        <FieldGroup label="Humidity (%)">
          <Input
            className="h-9 text-sm"
            placeholder="e.g. 78"
            value={humidityPercent}
            onChange={(e) => setHumidityPercent(e.target.value)}
          />
        </FieldGroup>
        <FieldGroup label="Coating Batch Number" required>
          <Input
            className="h-9 text-sm"
            placeholder="Coating batch number"
            value={coatingBatchNumber}
            onChange={(e) => setCoatingBatchNumber(e.target.value)}
          />
        </FieldGroup>
      </FormGrid>
      {isAddingAreaType && (
        <AddNewItemDialog
          open={isAddingAreaType}
          onOpenChange={setIsAddingAreaType}
          title="Add area type"
          label="Area type"
          placeholder="e.g. Internal field joint"
          onAdd={handleAddAreaType}
          buttonText="Add"
          cancelText="Cancel"
        />
      )}
      <SectionTitle>Coating Checklist</SectionTitle>
      {coatingTemplateLoading ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          Loading coating checklist…
        </p>
      ) : checklistSections.length === 0 ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          No coating checklist template found for this pipe yet. Add one from the Coating
          specification (Specifications → Coating → Add Category) so categories appear here.
        </div>
      ) : (
        <ProductionChecklistCards
          sections={checklistSections}
          values={checklistValues}
          noteValues={checklistNotes}
          onValueChange={updateChecklistValue}
          onNoteChange={updateChecklistNote}
        />
      )}

      <FieldGroup label="Notes / Observations">
        <textarea
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          rows={2}
          placeholder="Describe any observations..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </FieldGroup>

      <div className="space-y-2">
        <Label>Upload Coating Documents</Label>
        <p className="text-sm text-gray-600">
          Upload the coating inspection report and supporting documents.
          <br />
          <span className="text-xs text-gray-500">
            Allowed: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Text (.txt), Images (.jpg, .png, .gif)
            <br />
            Maximum file size: 100MB per file
          </span>
        </p>
        <SpoolStepSavedDocumentsList
          documents={persistedDocuments}
          rowUpdatedAt={coatingPanelRow?.updatedAt ?? null}
          downloadContext={downloadContext}
        />
        <FileUpload
          onFileSelect={handleMaterialFileSelect}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
          multiple={true}
          maxFiles={100}
          buttonText="Upload Documents"
          variant="outline"
          size="lg"
          onRemoveFile={handleRemoveFile}
          selectedFiles={files}
          showFileList={files.length > 0}
          className="w-full"
          showTags={true}
          optionsTags={tagOptions}
          selectedTags={selectedTags}
          showAddButton={true}
          addButtonText="Add New Tag"
          onAddButtonClick={() => setIsAddingDocTag(true)}
          onTagsChange={handleTagsChange}
          maxHeight=""
        />
        <div className="text-xs text-gray-500">
          Current files: {files.length} file(s)
        </div>
      </div>

      <AddNewItemDialog
        open={isAddingDocTag}
        onOpenChange={setIsAddingDocTag}
        title="Add New Tag"
        label="Tag Name"
        placeholder="Enter tag name"
        onAdd={handleAddDocTag}
        buttonText="Add Tag"
        cancelText="Cancel"
      />

      <Signature
        customerSignature={sig.selectedCoatingId}
        qcSignature={null}
        customerRepresentativeList={sig.coatingList}
        qcList={[]}
        handleCustomerSignatureChange={sig.onCoatingChange}
        signatureData={sig.coatingSignatureData}
        onSignatureChange={sig.onCoatingSignatureChange}
        showCustomerRep={true}
        showQc={false}
        customerRepLabel="Coating Inspector"
        canSign={true}
        canEdit={true}
        isEditMode={true}
        isAdminOrSuperAdmin={true}
      />
      <div className="flex justify-end gap-2 mt-2">
        <Button
          variant="outline"
          size="sm"
          type="button"
          className="border-red-300 text-red-600 hover:bg-red-50"
          disabled={!weld || savingDraft || savingRelease}
          onClick={() => onOpenRaiseNcr?.()}
        >
          Fail — Raise NCR
        </Button>
        <Button
          variant="outline"
          size="sm"
          type="button"
          disabled={draftDisabled}
          loading={savingDraft}
          loadingText="Saving draft…"
          onClick={() => void submit('draft')}
        >
          Save Draft
        </Button>
        <Button
          type="button"
          size="sm"
          className="bg-hero-red text-white hover:bg-hero-red-60"
          disabled={releaseDisabled || !sig.selectedCoatingId || !sig.coatingSignatureData}
          loading={savingRelease}
          loadingText={releaseLoadingText}
          onClick={() => void submit('pass_release')}
        >
          {actionText}
        </Button>
      </div>
    </CardContent>
    </Card>
    </div>
  );
}
